import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import {
  useGetChamberMemberProSettingsQuery,
  useGetCmpEventDetailQuery,
  useGetCmpEventTypesQuery,
  useUpdateCmpEventMutation,
} from "../../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import EventWizard from "../CreateEvent/wizard/EventWizard";
import { WizardAction } from "../CreateEvent/wizard/wizardShared";
import { ResultModal } from "../EventSales/eventSalesShared";
import {
  buildBlankEventFormState,
  eventFormStateToInput,
  eventReturnToFormState,
  toEventTypeOptions,
} from "../eventBackendMappers";
import { EventFormState } from "../eventMockData";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";

const getBrowserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Toronto";

const UpdateEvent = (): React.ReactElement => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [feedback, setFeedback] = useState<{
    success: boolean;
    title: string;
    message?: string;
  } | null>(null);
  // Incremented after each successful save so the wizard remounts against the
  // saved event and hasUnsavedChanges resets (EVT-PUBLISH-002).
  const [savedAtCounter, setSavedAtCounter] = useState(0);
  const [lastSavedEvent, setLastSavedEvent] = useState<{
    eventId: string;
    event: EventFormState;
  } | null>(null);

  const { data: detailData, loading: detailLoading } =
    useGetCmpEventDetailQuery({
      variables: { input: { chamberId, eventId: eventId || "" } },
      skip: !chamberId || !eventId,
      fetchPolicy: "cache-and-network",
    });
  const { data: eventTypesData, loading: eventTypesLoading } =
    useGetCmpEventTypesQuery({
      variables: { input: { chamberId, includeArchived: true } },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const { data: settingsData } = useGetChamberMemberProSettingsQuery({
    variables: { chamberId },
    skip: !chamberId,
  });
  const [updateCmpEvent, { loading: updatingEvent }] =
    useUpdateCmpEventMutation();

  const eventTypes = useMemo(
    () => toEventTypeOptions(eventTypesData?.getCmpEventTypes.eventTypes || []),
    [eventTypesData?.getCmpEventTypes.eventTypes]
  );
  const timeZone = normalizeCmpTimeZone(
    settingsData?.getChamberMemberProSettings.setup.timeZone ||
      detailData?.getCmpEventDetail.event?.timeZone ||
      getBrowserTimeZone()
  ).timeZone;
  const initialEvent = useMemo(() => {
    if (lastSavedEvent && lastSavedEvent.eventId === eventId) {
      return lastSavedEvent.event;
    }

    const event = detailData?.getCmpEventDetail.event;
    return event
      ? eventReturnToFormState(event, timeZone)
      : buildBlankEventFormState(eventTypes);
  }, [
    detailData?.getCmpEventDetail.event,
    eventId,
    eventTypes,
    lastSavedEvent,
    timeZone,
  ]);

  const handleSubmit = async ({
    event,
    action,
  }: {
    event: EventFormState;
    action: WizardAction;
  }) => {
    setFeedback(null);
    try {
      if (!chamberId || !eventId) {
        throw new Error("Event is still loading. Try again in a moment.");
      }
      const statusEvent: EventFormState = {
        ...event,
        status: action === "publish" ? "Published" : event.status,
      };
      const result = await updateCmpEvent({
        variables: {
          input: {
            chamberId,
            eventId,
            data: eventFormStateToInput({ event: statusEvent, timeZone }),
          },
        },
      });
      if (!result.data?.updateCmpEvent.eventSavedSuccessfully) {
        throw new Error("The event could not be updated.");
      }
      const savedEvent = result.data.updateCmpEvent.event;
      // The mutation response is the authoritative read-after-write result.
      // An immediate detail refetch can hit DynamoDB's eventually consistent
      // read path and remount the wizard with the pre-save time/options.
      setLastSavedEvent({
        eventId,
        event: savedEvent
          ? eventReturnToFormState(savedEvent, timeZone)
          : statusEvent,
      });
      setSavedAtCounter((count) => count + 1);
      setFeedback({
        success: true,
        title:
          action === "publish"
            ? "Your event has been published successfully!"
            : "Your event has been saved successfully!",
      });
    } catch (error) {
      const errorDetail = error instanceof Error ? error.message : "";
      setFeedback({
        success: false,
        title: "The event could not be updated.",
        message:
          errorDetail && errorDetail !== "The event could not be updated."
            ? errorDetail
            : undefined,
      });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (detailLoading || eventTypesLoading || !chamberId) {
    return (
      <div className="mx-auto w-full max-w-6xl rounded-lg border border-gray-200 bg-white px-5 py-4 text-sm font-semibold text-gray-600">
        Loading event setup...
      </div>
    );
  }

  if (!detailData?.getCmpEventDetail.event) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
        <p className="font-bold">Event not found.</p>
        <p className="mt-1">
          Choose an event from Manage Events to open the update workflow.
        </p>
        <button
          type="button"
          onClick={() => navigate(RoutePaths.chamberAdmin_events_manage)}
          className="mt-4 rounded-lg bg-cpaGold px-4 py-2 text-sm font-bold text-white transition hover:bg-amber-500"
        >
          Back to Manage Events
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Save/publish feedback uses the shared popup (EVT-PUBLISH-001) like
          the other event dashboard pages. */}
      <ResultModal
        open={Boolean(feedback)}
        success={feedback?.success ?? true}
        title={feedback?.title || ""}
        message={feedback?.message}
        onClose={() => setFeedback(null)}
      />
      <EventWizard
        key={`${eventId}-${savedAtCounter}`}
        mode="update"
        initialEvent={initialEvent}
        eventTypes={eventTypes}
        chamberId={chamberId}
        timeZone={timeZone}
        saving={updatingEvent}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default UpdateEvent;
